/* ──────────────────────────────────────────────────────────────
   src/api/reservas.ts
   Endpoints y tipos relacionados con reservas
   ────────────────────────────────────────────────────────────── */

import axios from "./axios"; // wrapper con interceptors & baseURL

/* ═════════════════════════════════════════════════════════════
   1) Tipos auxiliares
   ═════════════════════════════════════════════════════════════ */

/** Fila minimal que usa el informe de incidencias */
export type ReservaIncidencia = {
  id: number;
  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:mm
  estado: string; // puede ser code o label, según back
  cliente: string;
  comentariosInternos?: string | null;
};

/** Shape completo que devuelve GET /reservas/:id */
export type ReservaDetalle = {
  id: number;
  idProfesor: number;
  idClienteUsr: number;
  idAsignatura: number;
  idCentro?: number | null;
  idFormato: number;
  idProyecto?: number | null;
  proyecto?: string | null;

  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm

  /** code o label según disponibilidad en back (preferimos code si existe) */
  estado: string;

  language: "English" | "Spanish";
  incidencia: boolean;

  /* opcionales usados por el formulario / diálogo */
  numAlumnos?: number;
  alumnosIds?: number[];
  comentarios?: string | null;
  comentariosInternos?: string | null;
  curso?: string | null;
  examinationBoard?: string | null;
  linkAddress?: string | null;
  idRecurrencia?: string | null;

  /* etiquetas útiles en UI */
  nombreProf?: string | null;
  nombreAsignatura?: string | null;
  nombreCentro?: string | null;
  nombreFormato?: string | null;
  nombreProyecto?: string | null;
};

/** Respuesta paginada estándar del backend nuevo */
export type Paged<T> = {
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
    desde?: string;
    hasta?: string;
    order?: "asc" | "desc";
  };
  items: T[];
};

export function isPaged<T = any>(x: unknown): x is Paged<T> {
  return !!x && typeof x === "object" && Array.isArray((x as any).items) && (x as any).meta;
}

/* ═════════════════════════════════════════════════════════════
   2) Normalización de payloads
   ═════════════════════════════════════════════════════════════ */

function toHHmm(v?: string | null): string {
  if (!v) return "";
  const s = String(v);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function pickEstado(payload: any): string {
  // Preferimos el CODE si está disponible; si no, caemos a label
  return payload?.estado_code ?? payload?.estadoCode ?? payload?.estado ?? "";
}

function normalizeReservaDetalle(payload: any): ReservaDetalle {
  return {
    id: Number(payload?.id),
    idProfesor: Number(payload?.id_profesor ?? payload?.idProfesor),
    idClienteUsr: Number(payload?.id_cliente_usr ?? payload?.idClienteUsr),
    idAsignatura: Number(payload?.id_asignatura ?? payload?.idAsignatura),
    idCentro: payload?.id_centro ?? payload?.idCentro ?? null,
    idFormato: Number(payload?.id_formato ?? payload?.idFormato),
    idProyecto: payload?.id_proyecto ?? payload?.proyecto_id ?? payload?.idProyecto ?? null,

    fecha: String(payload?.fecha ?? ""),
    horaInicio: toHHmm(payload?.hora_inicio ?? payload?.horaInicio),
    horaFin: toHHmm(payload?.hora_fin ?? payload?.horaFin),

    estado: pickEstado(payload),

    language: (payload?.language ?? payload?.idioma ?? "English") as "English" | "Spanish",
    incidencia: Boolean(payload?.incidencia),

    numAlumnos: payload?.num_alumnos ?? payload?.numAlumnos,
    alumnosIds: payload?.alumnos_ids ?? payload?.alumnosIds,

    comentarios: payload?.comentarios ?? null,
    comentariosInternos: payload?.comentarios_internos ?? payload?.comentariosInternos ?? null,
    curso: payload?.student_year_grade ?? payload?.curso ?? null,
    examinationBoard: payload?.examination_board ?? payload?.examinationBoard ?? null,
    linkAddress: payload?.link_address ?? payload?.linkAddress ?? null,
    idRecurrencia: payload?.id_recurrencia ?? payload?.idRecurrencia ?? null,

    // etiquetas (si el endpoint las proporciona)
    nombreProf: payload?.nombre_profesor ?? payload?.nombreProf ?? null,
    nombreAsignatura: payload?.nombre_asignatura ?? payload?.nombreAsignatura ?? null,
    nombreCentro: payload?.nombre_centro ?? payload?.nombreCentro ?? null,
    nombreFormato: payload?.nombre_formato ?? payload?.nombreFormato ?? null,
    nombreProyecto: payload?.nombre_proyecto ?? payload?.nombreProyecto ?? null,
    proyecto: payload?.proyecto ?? null,
  } as ReservaDetalle;
}

/* ═════════════════════════════════════════════════════════════
   3) Endpoints
   ═════════════════════════════════════════════════════════════ */

/**
 * Detalle completo de una reserva (GET /reservas/:id).
 * Se utiliza para abrir el <ReservationForm> en modo edición/lectura.
 */
export async function getReserva(id: number): Promise<ReservaDetalle> {
  const { data } = await axios.get(`/reservas/${id}`);
  return normalizeReservaDetalle(data);
}

/**
 * Lista de reservas con incidencia de un profesor
 * entre `desde` y `hasta` (ambos inclusive).
 * GET /reservas/incidencias/:profesorId?desde=YYYY-MM-DD&hasta=YYYY-MM-DD
 */
export async function getReservasIncidencia(
  profesorId: number,
  desde: string,
  hasta: string
): Promise<ReservaIncidencia[]> {
  const { data } = await axios.get<ReservaIncidencia[]>(`/reservas/incidencias/${profesorId}`, {
    params: { desde, hasta },
  });
  return data.map((r) => ({
    ...r,
    horaInicio: toHHmm(r.horaInicio),
  }));
}

/**
 * Lista de reservas con incidencia de un alumno
 * GET /reservas/incidencias-alumno/:alumnoId?desde&hasta
 */
export async function getReservasIncidenciaAlumno(
  alumnoId: number,
  desde: string,
  hasta: string
): Promise<ReservaIncidencia[]> {
  const { data } = await axios.get<ReservaIncidencia[]>(
    `/reservas/incidencias-alumno/${alumnoId}`,
    { params: { desde, hasta } }
  );
  return data.map((r) => ({
    ...r,
    horaInicio: toHHmm(r.horaInicio),
  }));
}

/**
 * Cancelaciones (y afines) de un alumno en rango
 * GET /reservas/cancelaciones/:alumnoId?desde&hasta
 */
export async function getReservasCancelacion(
  alumnoId: number,
  desde: string,
  hasta: string
): Promise<ReservaIncidencia[]> {
  const { data } = await axios.get<ReservaIncidencia[]>(`/reservas/cancelaciones/${alumnoId}`, {
    params: { desde, hasta },
  });
  return data.map((r) => ({
    ...r,
    horaInicio: toHHmm(r.horaInicio),
  }));
}

/* ═════════════════════════════════════════════════════════════
   4) Utilidad opcional: traer TODO el listado autopaginando
   (por si quieres reutilizarlo en otros hooks/páginas)
   ═════════════════════════════════════════════════════════════ */

export type ReservasListParams = {
  desde: string;
  hasta: string;
  profesorId?: number | string;
  clienteIdUsr?: number | string;
  asignaturaId?: number | string;
  centroId?: number | string;
  curso?: string;
  estado?: string;
  estadoCode?: string;
  idFormato?: number | string;
  idProyecto?: number | string;
  aulaId?: number | string;
  order?: "asc" | "desc";
};

const MAX_PER_PAGE = 200;

/** Devuelve el array plano de items tras autopaginar el backend */
export async function listReservasAll(params: ReservasListParams): Promise<any[]> {
  let page = 1;
  const perPage = MAX_PER_PAGE;
  const out: any[] = [];

  while (true) {
    const { data } = await axios.get("/reservas", {
      params: { ...params, page, perPage, order: params.order ?? "asc" },
    });

    const chunk: any[] = Array.isArray((data as any)?.items)
      ? (data as any).items
      : Array.isArray((data as any)?.filas)
      ? (data as any).filas
      : Array.isArray((data as any)?.rows)
      ? (data as any).rows
      : Array.isArray(data)
      ? (data as any)
      : [];

    out.push(...chunk);

    const total = Number((data as any)?.meta?.total ?? out.length);
    if (!Number.isFinite(total) || out.length >= total) break;

    page += 1;
    if (page > Math.ceil(total / perPage) + 2) break; // salvaguarda
  }

  return out;
}
